import { assert } from '@takeshape/util';
import type { ProjectSchemaCompat } from 'app/compat/types';
import type { setCurrentProject } from 'app/project/actions';
import { produce } from 'immer';
import get from 'lodash/get';
import omit from 'lodash/omit';
import set from 'lodash/set';
import { type ActionType, createReducer } from 'typesafe-actions';
import {
  type BranchesAction,
  clearScratchState,
  closeBranchMerge,
  createBranchRoutine,
  deleteBranchRoutine,
  fetchSchemaRoutine,
  fetchServiceLayersRoutine,
  getBranchListRoutine,
  mergeBranchRoutine,
  openBranchMerge,
  postSchemaRoutine,
  resetBranchState,
  setBranchMergeCanMerge,
  setBranchMergeError,
  setBranchMergeInProgress,
  setCurrentBranch,
  setCurrentBranchCanPromote,
  setCurrentBranchNeedsUpdate,
  setProjectSchema,
  setUserSchema,
  setUseScratchForPreview,
  updateSchema
} from './actions';
import { isProductionTargetBranch } from './api.js';
import { DEFAULT_BRANCH, SCRATCH_BRANCH } from './constants.js';
import type { BranchesState } from './types.js';
import { createBranch, getBranchName, getSchemaPathFromAction, getSchemaPathFromState } from './utils.js';

export const initialState: BranchesState = {
  branches: {
    [DEFAULT_BRANCH]: createBranch(),
    [SCRATCH_BRANCH]: createBranch()
  },
  currentBranch: DEFAULT_BRANCH,
  currentBranchNeedsUpdate: null,
  currentBranchCanPromote: null,
  useScratchForPreview: false,
  branchMerge: {
    open: false,
    loading: false,
    inProgress: false,
    error: null,
    operation: null,
    branchName: null,
    mergePreview: null
  }
};

type HandledActions = BranchesAction | ActionType<typeof setCurrentProject>;

/**
 * Resets user schema and working copy after a deploy
 * @param draft an immer draft so updates stick
 * @param action
 * @param userSchema
 */
function updateAfterDeploy(draft: BranchesState, action: BranchesAction, userSchema: ProjectSchemaCompat) {
  draft.useScratchForPreview = false;

  set(draft, getSchemaPathFromAction(draft, action, 'user'), {
    data: userSchema,
    saving: false,
    loading: false
  });
}

export const reducer = createReducer<BranchesState, HandledActions>(initialState)
  .handleAction(resetBranchState, (): BranchesState => initialState)
  .handleAction(
    setCurrentBranch,
    (state, { payload }): BranchesState => ({
      ...state,
      currentBranch: payload.branch
    })
  )
  .handleAction(
    setUseScratchForPreview,
    (state, { payload }): BranchesState => ({
      ...state,
      useScratchForPreview: payload
    })
  )
  .handleAction(
    setUserSchema,
    (state, action): BranchesState =>
      produce(state, (draft) =>
        set(draft, getSchemaPathFromAction(state, action, 'user'), {
          data: action.payload.schema,
          saving: false,
          loading: false
        })
      )
  )
  .handleAction(
    setProjectSchema,
    (state, action): BranchesState =>
      produce(state, (draft) =>
        set(draft, getSchemaPathFromAction(state, action, 'runtime'), {
          data: action.payload.schema,
          saving: false,
          loading: false
        })
      )
  )
  .handleAction(fetchSchemaRoutine.pending, (state, action): BranchesState => {
    const path = getSchemaPathFromAction(state, action);
    const branchName = getBranchName(state, action);

    const result = produce(state, (draft) => {
      draft.branches[branchName] ||= createBranch();
      set(draft, path.concat('loading'), true);
    });

    return result;
  })
  .handleAction(fetchSchemaRoutine.fulfilled, (state, action): BranchesState => {
    return produce(state, (draft) => {
      const actions: any[] = [];

      if (action.meta.mode) {
        if (action.meta.mode === 'user' || action.meta.mode === 'all') {
          actions.push({ ...action, meta: { ...action.meta, runtime: false } });
        }

        if (action.meta.mode === 'runtime' || action.meta.mode === 'all') {
          actions.push({ ...action, meta: { ...action.meta, runtime: true } });
        }
      } else {
        actions.push(action);
      }

      for (const act of actions) {
        set(draft, getSchemaPathFromAction(state, act), {
          data: act.meta.runtime ? act.payload?.runtimeSchema : act.payload?.userSchema,
          loading: false
        });
      }

      return draft;
    });
  })
  .handleAction(fetchSchemaRoutine.rejected, (state, action): BranchesState => {
    const path = getSchemaPathFromAction(state, action);
    return produce(state, (draft) =>
      set(draft, path, {
        loading: false,
        error: action.payload
      })
    );
  })
  .handleAction(fetchServiceLayersRoutine.pending, (state, action): BranchesState => {
    const branchName = getBranchName(state, action);
    assert(state.branches[branchName], 'No branch in fetch service layers pending');
    return produce(state, (draft) => {
      draft.branches[branchName].serviceLayers.loading = true;
    });
  })
  .handleAction(fetchServiceLayersRoutine.fulfilled, (state, action): BranchesState => {
    const branchName = getBranchName(state, action);
    assert(state.branches[branchName], 'No branch in fetch service layers fulfilled');
    return produce(state, (draft) => {
      const { getAllServiceLayers } = action.payload;
      const { serviceLayers } = draft.branches[branchName];

      serviceLayers.loading = false;
      serviceLayers.hash = getAllServiceLayers.servicesHash;

      const { layers } = draft.branches[branchName];
      if (layers) {
        for (const { id, status, schema = null } of getAllServiceLayers.layers) {
          layers[id] = {
            data: schema,
            status,
            saving: false,
            loading: false
          };
        }
      }
    });
  })
  .handleAction(fetchServiceLayersRoutine.rejected, (state, action): BranchesState => {
    const branchName = getBranchName(state, action);
    return produce(state, (draft) => {
      draft.branches[branchName].serviceLayers = {
        loading: false,
        error: action.payload
      };
    });
  })
  .handleAction(fetchServiceLayersRoutine.cancelled, (state, action): BranchesState => {
    const branchName = getBranchName(state, action);
    return produce(state, (draft) => {
      draft.branches[branchName].serviceLayers.loading = false;
    });
  })
  .handleAction(postSchemaRoutine.pending, (state, action): BranchesState => {
    const path = getSchemaPathFromAction(state, action);
    return produce(state, (draft) => set(draft, path.concat('saving'), true));
  })
  .handleAction(postSchemaRoutine.fulfilled, (state, action): BranchesState => {
    const path = getSchemaPathFromAction(state, action);
    return produce(state, (draft) => {
      const { meta, payload } = action;
      if (meta.branch !== SCRATCH_BRANCH) {
        updateAfterDeploy(draft, action, payload.userSchema);
      }

      return set(draft, path, {
        data: payload.runtimeSchema,
        loading: false,
        saving: false
      });
    });
  })
  .handleAction(postSchemaRoutine.rejected, (state, action): BranchesState => {
    const path = getSchemaPathFromAction(state, action);
    return produce(state, (draft) => set(draft, path.concat('saving'), false));
  })
  .handleAction(
    updateSchema.pending,
    (state): BranchesState =>
      produce(state, (draft) => {
        const path = getSchemaPathFromState(state, 'runtime').concat('saving');
        return set(draft, path, true);
      })
  )
  .handleAction(
    updateSchema.fulfilled,
    (state, action): BranchesState =>
      produce(state, (draft) => {
        const { userSchema, runtimeSchema } = action.payload;
        updateAfterDeploy(draft, action, userSchema);
        const path = getSchemaPathFromState(state, 'runtime');
        return set(draft, path, {
          data: runtimeSchema,
          saving: false,
          loading: false
        });
      })
  )
  .handleAction(
    updateSchema.rejected,
    (state): BranchesState =>
      produce(state, (draft) => {
        const path = getSchemaPathFromState(state, 'runtime').concat('saving');
        return set(draft, path, false);
      })
  )
  .handleAction(
    createBranchRoutine.fulfilled,
    (state, { meta }): BranchesState => produce(state, (draft) => set(draft, ['branches', meta.name], createBranch()))
  )
  .handleAction(
    deleteBranchRoutine.fulfilled,
    (state, { meta }): BranchesState => ({
      ...state,
      branches: omit(state.branches, [meta.name])
    })
  )
  .handleAction(mergeBranchRoutine.fulfilled, (state, { meta }): BranchesState => {
    if (Boolean(meta.dryRun) || !isProductionTargetBranch(meta.target)) {
      return state;
    }

    return {
      ...state,
      branches: omit(state.branches, [meta.head.branchName])
    };
  })
  .handleAction(
    getBranchListRoutine.fulfilled,
    (state, { payload }): BranchesState =>
      produce(state, (draft) => {
        if (payload.branches) {
          payload.branches
            .filter(({ environment }) => environment === 'DEVELOPMENT')
            .forEach(({ branchName }) => {
              const currentData = get(draft, ['branches', branchName]);
              if (!currentData) {
                return set(draft, ['branches', branchName], createBranch());
              }
            });
        }
      })
  )
  .handleAction(openBranchMerge, (state, action): BranchesState => {
    return {
      ...state,
      branchMerge: {
        open: true,
        loading: true,
        inProgress: false,
        error: null,
        operation: action.payload.operation,
        branchName: action.payload.branchName,
        mergePreview: null
      }
    };
  })
  .handleAction(closeBranchMerge, (state): BranchesState => {
    return {
      ...state,
      branchMerge: {
        open: false,
        loading: false,
        inProgress: false,
        error: null,
        operation: null,
        branchName: null,
        mergePreview: null
      }
    };
  })
  .handleAction(setBranchMergeError, (state, action): BranchesState => {
    return {
      ...state,
      branchMerge: {
        ...state.branchMerge,
        error: action.payload
      }
    };
  })
  .handleAction(setBranchMergeCanMerge, (state, action): BranchesState => {
    return {
      ...state,
      branchMerge: {
        ...state.branchMerge,
        loading: false,
        mergePreview: action.payload
      }
    };
  })
  .handleAction(setBranchMergeInProgress, (state, action): BranchesState => {
    return {
      ...state,
      branchMerge: {
        ...state.branchMerge,
        inProgress: action.payload
      }
    };
  })
  .handleAction(setCurrentBranchNeedsUpdate, (state, action): BranchesState => {
    return {
      ...state,
      currentBranchNeedsUpdate: action.payload
    };
  })
  .handleAction(setCurrentBranchCanPromote, (state, action): BranchesState => {
    return {
      ...state,
      currentBranchCanPromote: action.payload
    };
  })
  .handleAction(clearScratchState, (state): BranchesState => {
    return {
      ...state,
      branches: {
        ...state.branches,
        scratch: createBranch()
      }
    };
  });
